import { useContext, useState, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { authService } from '../services/authService';

export const useAuth = () => {
  const context = useContext(AuthContext);

  const {
    user = null,
    userType = 'user',
    login: ctxLogin,
    logout: ctxLogout,
    refreshToken = async () => {},
    loading = false,
    otpState = {},
    sendOTP: ctxSendOTP,
    verifyOTP: ctxVerifyOTP,
    resendOTP: ctxResendOTP,
    resetOTP: ctxResetOTP,
    sendEmailOTP: ctxSendEmailOTP,
    verifyEmailOTP: ctxVerifyEmailOTP,
  } = context || {};

  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Enhanced login function with proper error handling
  const loginWithCredentials = useCallback(async (credentials) => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      const response = await authService.login(credentials);
      
      if (response.accessToken) {
        // Store token
        localStorage.setItem('auth_token', response.accessToken);
        // Store refresh token if provided
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        
        // Login user with token via context (if available)
        if (typeof ctxLogin === 'function') {
          ctxLogin(response.user, response.user?.role || 'user', response.accessToken, response.refreshToken);
        } else {
          console.error('Auth context not available: login');
        }
        
        return { success: true, user: response.user };
      } else {
        throw new Error('Login failed: No token received');
      }
    } catch (error) {
      setAuthError(error.message);
      // Clear any existing auth data on failed login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mgx_user');
      localStorage.removeItem('mgx_user_type');
      return { success: false, error: error.message };
    } finally {
      setAuthLoading(false);
    }
  }, [ctxLogin]);

// Enhanced register function with proper error handling
  // NOTE: Registration no longer auto-logins the user.
  // After successful registration, the user must log in (with OTP) via the normal login flow.
  const registerUser = useCallback(async (userData) => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      const response = await authService.register(userData);

      if (response && response.success) {
        // Do not call ctxLogin or store tokens here.
        return { success: true, user: response.user || null, message: response.message };
      }

      // If backend didn't mark success, surface a helpful error.
      const message = response?.error || response?.message || 'Registration failed';
      throw new Error(message);
    } catch (error) {
      // Prefer backend-provided error details when available
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Registration failed';

      setAuthError(message);
      // Ensure no leftover auth data from any previous sessions
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mgx_user');
      localStorage.removeItem('mgx_user_type');
      return { success: false, error: message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Enhanced logout function
  const logoutUser = useCallback(async () => {
    try {
      // Try to call logout API if user is authenticated
      const token = localStorage.getItem('auth_token');
      if (token) {
        await authService.logout();
      }
    } catch (error) {
      console.warn('Logout API call failed:', error.message);
    } finally {
      // Always clear local auth data
      if (typeof ctxLogout === 'function') {
        ctxLogout();
      }
      // Also clear local storage as a fallback
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    }
  }, [ctxLogout]);

  // Send default OTP for admin (as per guide)
  const sendDefaultOTPAdmin = useCallback(async (email) => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      const response = await authService.sendDefaultOTPAdmin(email);
      return { success: true, message: response.message };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Firebase social login (Google/Facebook via Firebase ID token)
  const loginWithFirebaseSocial = useCallback(async ({ idToken, provider }) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const response = await authService.firebaseSocialLogin({ idToken, provider });

      if (response && response.success && response.user && typeof ctxLogin === 'function') {
        ctxLogin(
          response.user,
          response.user.role || 'user',
          response.accessToken,
          response.refreshToken,
        );
      }

      return { success: !!response?.success, user: response.user || null };
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Social login failed';
      setAuthError(message);
      return { success: false, error: message };
    } finally {
      setAuthLoading(false);
    }
  }, [ctxLogin]);

  // Safe wrappers for OTP-related actions when context is missing
  const fallbackAsync = useCallback(async () => ({ success: false, error: 'Auth context not available' }), []);
  const sendOTPFn = ctxSendOTP ?? fallbackAsync;
  const verifyOTPFn = ctxVerifyOTP ?? fallbackAsync;
  const resendOTPFn = ctxResendOTP ?? fallbackAsync;
  const resetOTPFn = ctxResetOTP ?? fallbackAsync;
  const sendEmailOTPFn = ctxSendEmailOTP ?? fallbackAsync;
  const verifyEmailOTPFn = ctxVerifyEmailOTP ?? fallbackAsync;

  // Forgot password: request reset OTP via email
  const forgotPassword = useCallback(async (email) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const response = await authService.forgotPassword(email);
      if (!response.success && response.error) {
        setAuthError(response.error);
      }
      return response;
    } catch (error) {
      const message = error?.message || 'Failed to start password reset';
      setAuthError(message);
      return { success: false, error: message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Reset password using email + OTP + new password
  const resetPassword = useCallback(async ({ email, otp, password }) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const response = await authService.resetPassword({ email, otp, password });
      if (!response.success && response.error) {
        setAuthError(response.error);
      }
      return response;
    } catch (error) {
      const message = error?.message || 'Failed to reset password';
      setAuthError(message);
      return { success: false, error: message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  return {
    user,
    userType,
    login: loginWithCredentials,
    register: registerUser,
    logout: logoutUser,
    refreshToken,
    sendDefaultOTPAdmin,
    loading,
    authLoading,
    authError,
    // OTP related
    otpState,
    sendOTP: sendOTPFn,
    verifyOTP: verifyOTPFn,
    resendOTP: resendOTPFn,
    resetOTP: resetOTPFn,
    sendEmailOTP: sendEmailOTPFn,
    verifyEmailOTP: verifyEmailOTPFn,
    loginWithFirebaseSocial,
    // Password reset
    forgotPassword,
    resetPassword,
  };
};
